import { Link } from 'react-router-dom';

export interface ActivityItem {
  type: 'job' | 'invoice' | 'quote';
  id: number;
  ref: string;
  label: string;
  status: string;
  client: string;
  ts: string;
}

interface RecentActivityProps {
  items: ActivityItem[];
}

const JOB_STATUS: Record<string, { label: string; cls: string }> = {
  in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700' },
  scheduled: { label: 'Scheduled', cls: 'bg-warning/10 text-warning' },
  completed: { label: 'Completed', cls: 'bg-success/10 text-success' },
  quoted: { label: 'Quoted', cls: 'bg-primary/10 text-primary' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-500' },
};

const INV_STATUS: Record<string, { label: string; cls: string }> = {
  sent: { label: 'Sent', cls: 'bg-blue-50 text-blue-700' },
  viewed: { label: 'Viewed', cls: 'bg-primary/10 text-primary' },
  paid: { label: 'Paid', cls: 'bg-success/10 text-success' },
  overdue: { label: 'Overdue', cls: 'bg-critical/10 text-critical' },
  partial: { label: 'Partial', cls: 'bg-warning/10 text-warning' },
};

const QUOTE_STATUS: Record<string, { label: string; cls: string }> = {
  sent: { label: 'Sent', cls: 'bg-blue-50 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-success/10 text-success' },
  rejected: { label: 'Rejected', cls: 'bg-critical/10 text-critical' },
};

const TYPE_CONFIG = {
  job: { icon: 'construction', iconCls: 'bg-blue-50 text-blue-600', statusMap: JOB_STATUS, linkPrefix: '/jobs' },
  invoice: { icon: 'receipt_long', iconCls: 'bg-primary/10 text-primary', statusMap: INV_STATUS, linkPrefix: '/invoices' },
  quote: { icon: 'request_quote', iconCls: 'bg-success/10 text-success', statusMap: QUOTE_STATUS, linkPrefix: '/jobs' },
};

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const RecentActivity = ({ items }: RecentActivityProps) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
      <h3 className="text-base font-bold text-gray-900">Recent Activity</h3>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">No recent activity</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const cfg = TYPE_CONFIG[item.type];
            const statusInfo = cfg.statusMap[item.status];
            return (
              <Link
                key={`${item.type}-${item.id}`}
                to={`${cfg.linkPrefix}/${item.id}`}
                className="flex items-start gap-3 py-2.5 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg mt-0.5 ${cfg.iconCls}`}>
                  <span className="material-symbols-outlined text-sm leading-none">{cfg.icon}</span>
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.label}</p>
                  <p className="text-xs text-gray-500 truncate">{item.client || item.ref}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {statusInfo && (
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${statusInfo.cls}`}>
                      {statusInfo.label}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{timeAgo(item.ts)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
